import { Injectable } from '@angular/core';
import { escape } from 'lodash';
import { TranslateService } from '@ngx-translate/core';
import { COMPONENT_TYPE } from '../../constants/component_type';
import {
  AnyComponentData,
  DatabaseSourceComponentData,
  SalesforceSourceComponentData,
} from '../../package-designer/package.models';
import { ComponentTypeItem } from '../../constants/component_types';

export interface Tag {
  name: string;
}

function getValueName(key: string, value: any): string {
  let newValue = value;
  const $label = document.querySelector(`[for="${key}"]`);
  if (!$label) {
    return '';
  }
  const $next = $label.nextElementSibling;
  if ($next.matches('.btn-group-select')) {
    if (typeof value === 'object') {
      const keys = Object.keys(value);
      if (keys.length > 0) {
        newValue = ($next.querySelector(`[value="${keys[0]}"]`) as HTMLElement).innerText;
      }
    } else {
      newValue = ($next.querySelector(`[value="${value}"]`) as HTMLElement).innerText;
    }
  } else if ($next.matches('xp-select')) {
    newValue = ($next.querySelector('.mat-select-value') as HTMLElement).innerText;
  } else if ($next.matches('.form-group-options')) {
    if (!$next.querySelector('input[type=radio]:checked')) {
      newValue = '';
    } else {
      newValue = ($next.querySelector('input[type=radio]:checked').nextElementSibling as HTMLElement).innerText;
    }
  }
  return typeof newValue === 'object' ? '' : newValue;
}

function byString(o: any, s: string): any {
  let newS = s;
  let newO = o;
  newS = newS.replace(/\[(\w+)\]/g, '.$1');
  newS = newS.replace(/^\./, '');
  const a = newS.split('.');
  for (let i = 0, n = a.length; i < n; i += 1) {
    const k = a[i];
    if (k in o) {
      newO = o[k];
    } else {
      return null;
    }
  }
  return newO;
}

function generateTags(
  key: string,
  label: string,
  component: AnyComponentData,
  translationPath?: string,
  translate?: TranslateService,
): Tag[] {
  const tags: Tag[] = [];
  const value = byString(component, key);
  if (translationPath && value) {
    tags.push({
      name: `<span>${escape(label)}</span>: <b>${escape(translate.instant(translationPath + value))}</b>`,
    });
    return tags;
  }
  if (component[key] && component[key].length > 0) {
    tags.push({
      name: `<span>${escape(label)}</span>: <b>${escape(getValueName(key, component[key]))}</b>`,
    });
  } else if (value || value === '') {
    if (value === '') {
      return tags;
    }
    tags.push({ name: `<span>${escape(label)}</span>: <b>${escape(getValueName(key, value))}</b>` });
  } else if (label) {
    tags.push({ name: `<b>${escape(label)}</b>` });
  }

  return tags;
}

@Injectable({
  providedIn: 'root',
})
export class ComponentFormTagsService {
  constructor(private translate: TranslateService) {}
  public getTags(component: AnyComponentData, componentItem: ComponentTypeItem): Tag[] {
    let tags: Tag[] = [];

    // eslint-disable-next-line default-case
    switch (componentItem.componentType) {
      case COMPONENT_TYPE.CLOUD_STORAGE_SOURCE_COMPONENT:
        if (component.connection && ['softlayer', 'rackspace', 'swift'].indexOf(component.connection.type) > -1) {
          tags = [...tags, ...generateTags('bucket', 'Container', component)];
        } else if (component.connection && ['s3', 'gs'].indexOf(component.connection.type) > -1) {
          tags = [...tags, ...generateTags('bucket', 'Bucket', component)];
        }
        tags = [...tags, ...generateTags('path', 'Path', component)];
        tags = [...tags, ...generateTags('record_delimiter', 'Record delimiter', component)];
        tags = [...tags, ...generateTags('record_type', 'Record type', component)];
        if (component.record_type === 'delimited') {
          tags = [...tags, ...generateTags('record_settings.field_delimiter', 'Field delimiter', component)];
          tags = [...tags, ...generateTags('record_settings.text_qualifier', 'String qualifier', component)];
        }
        if (component.record_delimiter === 'eof' && component.record_type === 'json') {
          tags = [...tags, ...generateTags('json_path', 'Json path', component)];
        }
        if (
          component.connection &&
          (component.connection.type === 's3' ||
            component.connection.type === 'gs' ||
            component.connection.type === 'sftp' ||
            component.connection.type === 'ftps' ||
            component.connection.type === 'azureblobstorage')
        ) {
          tags = [...tags, ...generateTags('before_action', 'Source action', component)];

          if (component.before_action === 'copy' || component.before_action === 'copy_manifest')
            tags = [...tags, ...generateTags('char_encoding', 'Character Encoding', component)];
        }
        break;

      case COMPONENT_TYPE.DATABASE_SOURCE_COMPONENT:
        tags = [...tags, ...generateTags('access_mode', 'Access mode', component)];
        if ((component as DatabaseSourceComponentData).access_mode === 'table') {
          tags = [...tags, ...generateTags('schema_name', 'Schema', component)];
          tags = [...tags, ...generateTags('table_name', 'Table/view', component)];
          tags = [...tags, ...generateTags('where_clause', 'Where clause', component)];
          tags = [...tags, ...generateTags('split_by_column_name', 'Split query by key column', component)];
        } else {
          tags = [...tags, ...generateTags('query', 'Query', component)];
        }
        if (component.split_by_column_name?.length > 0) {
          tags = [...tags, ...generateTags('parallel', 'Max parallel connections', component)];
        }

        tags = [...tags, ...generateTags('before_action', 'Source action', component)];
        break;

      case COMPONENT_TYPE.AMAZON_REDSHIFT_SOURCE_COMPONENT:
        tags = [...tags, ...generateTags('access_mode', 'Access mode', component)];
        if ((component as DatabaseSourceComponentData).access_mode === 'table') {
          tags = [...tags, ...generateTags('schema_name', 'Schema', component)];
          tags = [...tags, ...generateTags('table_name', 'Table', component)];
          tags = [...tags, ...generateTags('where_clause', 'Where clause', component)];
        } else {
          tags = [...tags, ...generateTags('query', 'Query', component)];
        }
        break;

      case COMPONENT_TYPE.SPANNER_SOURCE_COMPONENT:
      case COMPONENT_TYPE.BIG_QUERY_SOURCE_COMPONENT:
        tags = [...tags, ...generateTags('access_mode', 'Access mode', component)];
        if ((component as DatabaseSourceComponentData).access_mode === 'table') {
          tags = [...tags, ...generateTags('table_name', 'Table', component)];
        } else {
          tags = [...tags, ...generateTags('query', 'Query', component)];
        }
        break;
      case COMPONENT_TYPE.NET_SUITE_SOURCE_COMPONENT:
        tags = [...tags, ...generateTags('table_name', 'Table', component)];
        break;
      case COMPONENT_TYPE.MONGO_SOURCE_COMPONENT:
        tags = [...tags, ...generateTags('table_name', 'Source collection', component)];
        tags = [...tags, ...generateTags('where_clause', 'Filter query', component)];
        break;

      case COMPONENT_TYPE.CLOUD_STORAGE_DESTINATION_COMPONENT:
        if (component.connection && ['s3', 'gs'].indexOf(component.connection.type) > -1) {
          tags = [...tags, ...generateTags('bucket', 'Bucket', component)];
        }
        tags = [...tags, ...generateTags('path', 'Directory', component)];
        tags = [...tags, ...generateTags('file_names', 'File pattern', component)];

        if (component.operation_type === 'copy_files') {
          tags = [...tags, ...generateTags('char_encoding', 'Character Encoding', component)];
        }

        break;

      case COMPONENT_TYPE.BING_ADS_SOURCE_COMPONENT:
        tags = [...tags, ...generateTags('api_version', 'API version', component)];
        tags = [...tags, ...generateTags('report_type', 'Report type', component)];
        tags = [...tags, ...generateTags('report_aggregation_type', 'Unit of time', component)];
        tags = [...tags, ...generateTags('report_date_range_type', 'Date range', component)];
        tags = [...tags, ...generateTags('report_on', 'Report on', component)];
        break;

      case COMPONENT_TYPE.ANALYTICS_SOURCE_COMPONENT:
        tags = [...tags, ...generateTags('report_on', 'Report on', component)];
        tags = [...tags, ...generateTags('report_date_range_type', 'Date Range', component)];

        if (component.report_date_range_type === 'CUSTOM_DATE') {
          tags = [...tags, ...generateTags('report_date_range_min', 'From date', component)];
          tags = [...tags, ...generateTags('report_date_range_max', 'To date', component)];
        }
        break;
      case COMPONENT_TYPE.ADWORDS_SOURCE_COMPONENT:
        tags = [...tags, ...generateTags('report_type', 'Report type', component)];
        tags = [...tags, ...generateTags('report_date_range_type', 'Date Range', component)];

        if (component.report_date_range_type === 'CUSTOM_DATE') {
          tags = [...tags, ...generateTags('report_date_range_min', 'From date', component)];
          tags = [...tags, ...generateTags('report_date_range_max', 'To date', component)];
        }
        tags = [...tags, ...generateTags('customer_ids', 'Customer ids', component)];
        break;
      case COMPONENT_TYPE.SALESFORCE_SOURCE_COMPONENT:
        tags = [...tags, ...generateTags('access_mode', 'Access mode', component)];
        if ((component as SalesforceSourceComponentData).access_mode === 'object') {
          tags = [...tags, ...generateTags('object_name', 'Object', component)];
        } else {
          tags = [...tags, ...generateTags('query', 'Query', component)];
        }

        break;
      case COMPONENT_TYPE.SALESFORCE_DESTINATION_COMPONENT:
        tags = [...tags, ...generateTags('object_name', 'Object', component)];
        tags = [...tags, ...generateTags('operation_type', 'Operation', component)];

        break;

      case COMPONENT_TYPE.FACEBOOK_ADS_INSIGHTS_SOURCE_COMPONENT:
        // eslint-disable-next-line no-case-declarations
        let accountsText;

        if (component.report_on === 'all') {
          accountsText = 'all accounts';
        } else {
          accountsText = `${
            component.ad_accounts.length + (component.ad_accounts.length === 1 ? ' account' : ' accounts')
          } selected`;
        }

        tags.push({ name: `${`<span>Ad accounts</span>: <b>`}${accountsText}</b>` });

        tags = [...tags, ...generateTags('level', 'Level', component)];
        tags = [...tags, ...generateTags('report_date_range_type', 'Date Range', component)];

        if (component.report_date_range_type === 'CUSTOM_DATE') {
          tags = [...tags, ...generateTags('report_date_range_min', 'From date', component)];
          tags = [...tags, ...generateTags('report_date_range_max', 'To date', component)];
        }

        break;
      case COMPONENT_TYPE.REST_API_SOURCE_COMPONENT:
        tags = [...tags, ...generateTags('method', 'Method', component)];
        if (component.url && component.url.length > 0) {
          tags = [...tags, ...generateTags('url', 'URL', component)];
        }
        if (component.use_pagination) {
          tags = [...tags, ...generateTags('', 'Use pagination', component)];
        }
        tags = [
          ...generateTags(
            'response_type',
            'Response type',
            component,
            'rest-api-source-editor.form.selects.response_type.options.',
            this.translate,
          ),
        ];
        break;

      case COMPONENT_TYPE.JOIN_COMPONENT:
        tags = [...tags, ...generateTags('join_type', 'Type', component)];
        tags = [...tags, ...generateTags('optimization_type', 'Optimization', component)];
        break;

      case COMPONENT_TYPE.SNOWFLAKE_DESTINATION_COMPONENT:
      case COMPONENT_TYPE.AMAZON_REDSHIFT_DESTINATION_COMPONENT:
      case COMPONENT_TYPE.DATABASE_DESTINATION_COMPONENT:
      case COMPONENT_TYPE.BIG_QUERY_DESTINATION_COMPONENT:
      case COMPONENT_TYPE.SPANNER_DESTINATION_COMPONENT:
        if (component.warehouse && component.warehouse.length > 0) {
          tags = [...tags, ...generateTags('warehouse', 'Warehouse', component)];
        }
        if (component.database && component.database.length > 0) {
          tags = [...tags, ...generateTags('database', 'Database', component)];
        }
        if (component.schema_name && component.schema_name.length > 0) {
          tags = [...tags, ...generateTags('schema_name', 'Schema', component)];
        }
        tags = [...tags, ...generateTags('table_name', 'Table', component)];
        tags = [...tags, ...generateTags('operation_type', 'Operation', component)];
        break;
      case COMPONENT_TYPE.MONGO_DESTINATION_COMPONENT:
        tags = [...tags, ...generateTags('table_name', 'Collection', component)];
        tags = [...tags, ...generateTags('operation_type', 'Operation', component)];
        break;
    }

    if (
      componentItem.componentType === COMPONENT_TYPE.BIG_QUERY_SOURCE_COMPONENT &&
      component.access_mode === 'query'
    ) {
      tags = [...tags, ...generateTags('use_legacy_sql', 'Use Legacy SQL', component)];
      tags = [...tags, ...generateTags('stored_procedure', 'Stored Procedure Query', component)];
    }

    if (
      (componentItem.componentType === COMPONENT_TYPE.SPANNER_SOURCE_COMPONENT ||
        componentItem.componentType === COMPONENT_TYPE.NET_SUITE_SOURCE_COMPONENT) &&
      (component as DatabaseSourceComponentData).access_mode === 'table'
    ) {
      tags = [...tags, ...generateTags('where_clause', 'Where clause', component)];
    }

    return tags;
  }
}
